import { ThemedFixedButton } from "@/Button";
import { Checkbox, ValidateCheckbox } from "@/Checkbox";
import { Divider } from "@/Divider";
import { Header } from "@/Header";
import { ValidateInput } from "@/Input";
import { SequentialAnimation } from "@/SequentialAnimation";
import { useAppColor } from "^/common/useAppColor";
import { useStorage } from "^/common/useStorage";
import { SignType } from "#/unique/domain";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { Controller, useForm } from "react-hook-form";
import { businessAppLogin, useBusinessTeacherSignUp } from "~/services/auth";
import { SignupContext } from "@/SignupContext";
import { SHA256 } from "crypto-js";
import { Dialog } from "@/Dialog";
import MarketingConsentContent from "@/common/MarketingConsentContent";
import { TermsDocContent } from "@/common/TermsDocContent";

export const Route = createFileRoute("/sign-up/business-teacher")({
  component: RouteComponent,
});

interface SignUpBusinessTeacherState {
  email?: string;
  name: string;
  phone: string;
  agree1: boolean;
  agree2: boolean;
}

function RouteComponent() {
  const [cookie] = useCookies(["email", "name"]);
  const [snsId] = useStorage<string>("local-sns-id");
  const [snsType] = useStorage<SignType>("local-sign-sns-type");
  const [fcm] = useStorage<string>("session-fcm");
  const { setInfo } = useContext(SignupContext);
  const email: string = useMemo(() => cookie.email, [cookie]);
  const name: string = useMemo(() => cookie.name, [cookie]);
  const { history } = useRouter();
  const { mutate: signUp, isPending } = useBusinessTeacherSignUp();

  // Dialog 상태 관리
  const [isErrorDialogVisible, setIsErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitted },
    watch,
  } = useForm<SignUpBusinessTeacherState>({
    defaultValues: {
      name: name || "",
    },
  });
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [isMarketingModalVisible, setIsMarketingModalVisible] = useState(false);
  // const [showValidationErrorDialog, setShowValidationErrorDialog] =
  //   useState(false);
  const allCheck = watch("agree1") && watch("agree2");
  const handleAllCheck = () => {
    if (isSubmitted || !allCheck) {
      setValue("agree1", !allCheck, { shouldValidate: true });
      setValue("agree2", !allCheck, { shouldValidate: true });
    } else {
      setValue("agree1", !allCheck);
      setValue("agree2", !allCheck);
    }
  };

  // 에러 Dialog 닫기 및 디폴트 화면으로 이동
  const handleErrorDialogClose = () => {
    setIsErrorDialogVisible(false);
    history.push("/");
  };
  const bindSubmit = (data: SignUpBusinessTeacherState) => {
    console.log(data);

    // SignupInfo 타입으로 변환하여 Context에 저장
    const signupInfo = {
      email: data.email || email,
      name: data.name,
      phone: data.phone,
      agree1: data.agree1,
      agree2: data.agree2,
    };
    setInfo(signupInfo);

    // API 요청 데이터 준비 (새로운 형식)
    const signUpData = {
      email: data.email || email,
      name: data.name,
      phone: data.phone,
      agree1: data.agree1,
      agree2: data.agree2,
      snsId: snsId || "",
      snsType: snsType || "kakao",
    };

    // 비즈니스 강사 회원가입 API 호출
    signUp(signUpData, {
      onSuccess: async (response: any) => {
        console.log("회원가입 성공:", response);
        const userAgent = navigator.userAgent;
        const isApp = /MT_I|MT_A/i.test(userAgent);
        if (response.statusCode === 200) {
          if (isApp) {
            // 회원가입 성공 후 앱 로그인 API 호출
            const id = snsId || "";
            const key = snsType + id;
            const hash = SHA256(key).toString();
            try {
              const appLoginData = {
                cdSnsType: snsType || "",
                hash: hash,
                fcmToken: fcm || "",
                cdMobileType: /MT_A/i.test(userAgent) ? "ANDROID" : "IOS",
              };
              const loginResponse = await businessAppLogin(appLoginData);
              console.log("앱 로그인 성공:", loginResponse);

              // 앱 로그인 성공 시 조건에 따른 리다이렉트
              if (loginResponse?.statusCode === 200) {
                // 로그인 성공 시 비즈니스 메인 페이지로 이동
                // alert("앱 로그인 성공");
                history.push("/sign-up/business-teacher-done");
              } else {
                // 로그인 실패 시 회원가입 완료 페이지로 이동
                // alert("앱 로그인 실패");
                history.push("/");
              }
            } catch (error) {
              console.error("앱 로그인 실패:", error);
              // 앱 로그인이 실패해도 회원가입은 성공했으므로 다음 페이지로 이동
              // history.push("/sign-up/business-owner-done");
            }
          } else {
            history.push("/sign-up/business-teacher-done");
          }
        } else {
          // 회원가입 실패 시 에러 메시지 표시
          setErrorMessage(response.message);
          setIsErrorDialogVisible(true);
        }
      },
      onError: (error: any) => {
        console.error("강사 회원가입 실패:", error);
        // 에러 처리 로직 추가 가능
        setErrorMessage("회원가입 중 오류가 발생했습니다. 다시 시도해주세요.");
        setIsErrorDialogVisible(true);
      },
    });
  };

  useAppColor({
    background: "#ff9934",
  });

  const handlePrivacyModalClose = () => {
    setIsPrivacyModalVisible(false);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalVisible(false);
  };

  const handleMarketingModalClose = () => {
    setIsMarketingModalVisible(false);
  };
  return (
    <div className="flex flex-col flex-auto">
      <Header title="회원가입" className="text-white bg-yellow" />
      <form className="bg-white flex flex-col flex-auto rounded-t-3xl pt-10 px-5 shadow-[0_-15px_60px_-15px_rgba(0,0,0,0.3)] gap-4">
        <SequentialAnimation>
          <ValidateInput
            className="w-full"
            placeholder="이메일 주소를 입력하세요"
            label="이메일 주소"
            disabled={true}
            value={email}
          />
          <ValidateInput
            className="w-full"
            label="이름"
            placeholder="이름을 입력하세요"
            errorMessage={errors?.name?.message}
            {...register("name", {
              required: { value: true, message: "이름을 입력하세요" },
              minLength: {
                value: 2,
                message: "이름은 최소 2글자 이상이어야 합니다",
              },
              pattern: {
                value: /^[가-힣]+$/,
                message: "한글만 입력 가능합니다",
              },
            })}
          />
          <ValidateInput
            className="w-full"
            label="휴대폰번호"
            type="tel"
            errorMessage={errors?.phone?.message}
            {...register("phone", {
              required: { value: true, message: "휴대폰 번호를 입력하세요" },
              pattern: {
                value: /^\d{3}-\d{3,4}-\d{4}$/,
                message: "유효한 휴대폰 번호를 입력하세요",
              },
            })}
          />
        </SequentialAnimation>
        <Divider className="mt-auto" />

        <SequentialAnimation initialDelay={0.2}>
          <Checkbox
            title="전체 동의"
            checked={allCheck}
            onChange={handleAllCheck}
          />
          <Divider line />
          <Controller
            name="agree1"
            control={control}
            rules={{ required: "필수 동의입니다." }}
            render={({ field }) => (
              <ValidateCheckbox
                defaultChecked={field.value}
                onChange={() => field.onChange(!field.value)}
                errorMessage={errors?.agree1?.message}
                title="[필수] 멍투데이 서비스 이용 약관 및 개인정보처리방침에 동의합니다."
              />
            )}
          />
          <div className="flex gap-2 pl-8">
            <p
              onClick={() => setIsTermsModalVisible(true)}
              className="text-sm mt-0.5 text-orange-400 cursor-pointer"
            >
              [이용약관]
            </p>
            <p
              onClick={() => setIsPrivacyModalVisible(true)}
              className="text-sm mt-0.5 text-orange-400 cursor-pointer"
            >
              [개인정보처리방침]
            </p>
          </div>
          <Controller
            name="agree2"
            control={control}
            render={({ field }) => (
              <ValidateCheckbox
                defaultChecked={field.value}
                onChange={() => field.onChange(!field.value)}
                errorMessage={errors?.agree2?.message}
                title="[선택] 광고성 정보 수신 전체 동의"
                desc="마케팅정보 수집에 동의하시면 유치원 할인, 프로모션 정보 등을 받아보실 수 있습니다."
              />
            )}
          />
          <p
            onClick={() => setIsMarketingModalVisible(true)}
            className="text-sm mt-0.5 text-orange-400 pl-8 cursor-pointer"
          >
            [마케팅정보 수신 동의]
          </p>
        </SequentialAnimation>
      </form>
      <ThemedFixedButton
        disabled={isPending}
        onClick={handleSubmit(bindSubmit)}
        color="primary"
        size="md"
      >
        {isPending ? "가입 중..." : "약관 동의하고 가입하기"}
      </ThemedFixedButton>
      {/* 개인정보처리방침 모달 */}
      <Dialog visible={isPrivacyModalVisible} onClose={handlePrivacyModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">개인정보처리방침</h2>
            <button
              onClick={handlePrivacyModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent targetRole="BUSINESS" termsType="TERMS_TYPE_PRIVACY" />
          </div>
        </div>
      </Dialog>
      {/* 이용약관 모달 */}
      <Dialog visible={isTermsModalVisible} onClose={handleTermsModalClose}>
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">이용약관</h2>
            <button
              onClick={handleTermsModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <TermsDocContent targetRole="BUSINESS" termsType="TERMS_TYPE_SERVICE" />
          </div>
        </div>
      </Dialog>
      {/* 마케팅정보 수신 동의 모달 */}
      <Dialog
        visible={isMarketingModalVisible}
        onClose={handleMarketingModalClose}
      >
        <div className="flex flex-col max-h-[60vh] w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="ml-4 text-lg font-bold">마케팅 정보 수신동의</h2>
            <button
              onClick={handleMarketingModalClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto w-full h-full">
            <MarketingConsentContent />
          </div>
        </div>
      </Dialog>

      {/* 에러 Dialog */}
      <Dialog visible={isErrorDialogVisible} onClose={handleErrorDialogClose}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">회원가입 실패</h3>
          <p className="mb-4 text-gray-600">{errorMessage}</p>
          <button
            onClick={handleErrorDialogClose}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </div>
  );
}
